import type { ViewFieldsFragment } from '@salescore/client-api'
import type { ChangeHistory } from '@salescore/client-redux'
import type {
  EltChange,
  EltChangeResult,
  EltChangeResultsChunk,
  ViewQueryRecordNode,
  ViewRecordFieldChangeItem,
} from '@salescore/core'
import { message } from 'antd'
import { Array, HashMap, Option, pipe, Predicate, Record } from 'effect'
import { t } from 'i18next'
import { type Dispatch, type SetStateAction, useCallback, useMemo } from 'react'
import { atom, useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'

import type { StateDispatcher } from '../domain/binder'
import { bindRecordsHistoryActions } from '../domain/recordsHistory/recordsHistoryActions'
import type { RecordsHistory } from '../domain/recordsHistory/recordsHistoryTypes'
import { partitionChangeResults, restoreFailedChanges } from '../domain/recordsHistory/restoreFailedChanges'
import { useRecordsImpl, useSetRecordsImpl } from './records'
import { errorsAtom } from './records/atoms'
import { viewsContextAtom } from './view/atoms'

// 変更差分
//
export const changesAtom = atom<ChangeHistory[]>({
  key: `view/changes`,
  default: [],
})

const backedChangesAtom = atom<ChangeHistory[]>({
  key: `view/backedChanges`,
  default: [],
})

const useRecordsHistory = () => {
  const records = useRecordsImpl()
  const setRecords = useSetRecordsImpl()
  const [changes, setChanges] = useRecoilState(changesAtom)
  const [backedChanges, setBackedChanges] = useRecoilState(backedChangesAtom)

  const setRecordsHistory: StateDispatcher<RecordsHistory> = useCallback(
    (valueOrTransformer) => {
      const newValue = Predicate.isFunction(valueOrTransformer)
        ? valueOrTransformer({ records, changes, backedChanges })
        : valueOrTransformer
      setRecords(newValue.records)
      setChanges(newValue.changes)
      setBackedChanges(newValue.backedChanges)
    },
    [backedChanges, changes, records, setBackedChanges, setChanges, setRecords],
  )
  return useMemo(() => bindRecordsHistoryActions(setRecordsHistory), [setRecordsHistory])
}

export const useForwardHistory = () => {
  const { forwardHistory } = useRecordsHistory()

  return (): void => {
    forwardHistory()
  }
}

export const useBackHistory = () => {
  const { backHistory } = useRecordsHistory()

  return (backCount: number): void => {
    backHistory(backCount)
  }
}
export type ChangesWithTransactionUpsertStatus =
  | { status: 'notUpserted' }
  | { status: 'upsertError'; error: unknown }
  | { status: 'upserted'; results: EltChangeResultsChunk[] }

function replaceRecordId(
  record: ViewQueryRecordNode,
  successfullCreateRecordIdMap: Record<string, string>,
  alreadyReplacedRecordIds: Set<string>,
  depth: number,
): ViewQueryRecordNode {
  if (Object.keys(successfullCreateRecordIdMap).length === alreadyReplacedRecordIds.size || depth > 10) {
    return record
  }
  const updatedRecordChildren = record.children.map((tableChild) => {
    const updatedTableChildren = tableChild.children.map((recordChild) =>
      replaceRecordId(recordChild, successfullCreateRecordIdMap, alreadyReplacedRecordIds, depth + 1),
    )
    return { ...tableChild, children: updatedTableChildren }
  })
  if (record.id === undefined || successfullCreateRecordIdMap[record.id] === undefined) {
    return { ...record, children: updatedRecordChildren }
  }

  const updatedRecordId = successfullCreateRecordIdMap[record.id]
  alreadyReplacedRecordIds.add(record.id)
  return { ...record, id: updatedRecordId, children: updatedRecordChildren }
}

export const useChangesMutation = (view: ViewFieldsFragment) => {
  const [changes, setChanges] = useRecoilState(changesAtom)
  const setBackedChanges = useSetRecoilState(backedChangesAtom)
  const records = useRecordsImpl()
  const setRecords = useSetRecordsImpl()
  const setErrors = useSetRecoilState(errorsAtom)
  const { updateCachedView } = useRecoilValue(viewsContextAtom)
  const hasHubspotData = changes.some((x) => x.changes.some((y) => y.streamName.startsWith('hubspot_')))

  return {
    // eslint-disable-next-line sonarjs/cognitive-complexity
    async resetChangesWithTransaction(
      callback: () => Promise<ChangesWithTransactionUpsertStatus>,
      setRecordSaveErrors: Dispatch<SetStateAction<EltChangeResult[]>>,
    ): Promise<Array<{ status: 'success'; change: EltChange; result: { id: string } }> | undefined> {
      const changesToSave = [...changes]
      const result = await callback()
      if (result.status !== 'upserted') {
        // upserted でないときは、 changesAtom の中身はそのままにしておく。
        // つまり、保存処理に渡した changes と保存処理中に新たに作られた changes を合わせたものが changesAtom の中身。
        if (result.status === 'notUpserted') {
          return
        }
        // バックエンド側でtry-catchしているので、ここでエラーになることはネットワークエラー系のみのはず
        const { error } = result
        setErrors([JSON.stringify(error)])
        const error_ = error instanceof Error ? error : new Error(`エラーが発生しました`)
        throw error_
      }
      // upsertedなとき、エラーになったレコードが存在すれば、それだけを復元する
      const { successes, errors, temporaryToRealRecordIdMap } = partitionChangeResults(result.results)
      if (successes.length > 0) {
        updateCachedView?.({ viewId: view.id, shouldIgnoreRefetchCache: true })
      }

      if (errors.length === 0) {
        const successMessage = hasHubspotData
          ? t(
              `{{length}}件のレコードを保存しました。データの反映がされない場合はお待ちいただくか、時間を空けて同期してください`,
              { length: successes.length },
            )
          : t(`{{length}}件のレコードを保存しました`, { length: successes.length })
        void message.success(successMessage)
        setRecordSaveErrors([])
        return successes
      }
      const applyRealRecordIdResults = restoreFailedChanges({ changesToSave, errors, temporaryToRealRecordIdMap })
      const changesWithError = pipe(
        applyRealRecordIdResults,
        Array.map((x) => x.change),
      )
      setChanges(changesWithError)
      updateCachedView?.({ viewId: view.id, changes: changesWithError })

      if (successes.length > 0) {
        void message.success(t(`{{length}}件のレコードが正常に保存されました。`, { length: successes.length }))
      }
      const errorSummaries = [t(`{{length}}件のレコードでエラーが発生しました。`, { length: errors.length })]
      if (applyRealRecordIdResults.some((x) => x.mapErrors.length > 0)) {
        errorSummaries.push(t(`新規作成されたレコードの ID を編集中のレコードに適用する処理に失敗しました。`))
      }
      const unknownCreateErrors = errors.filter((x) => x.status === 'unknown').filter((x) => x.change.type === 'create')
      if (unknownCreateErrors.length > 1) {
        errorSummaries.push(
          t(`複数の新規レコード作成中にエラーが発生しました。このまま保存すると重複して保存される可能性があります。`),
        )
      }

      // 正常に保存されたレコードとエラーが混在している場合、 refetch が走らないので正常に保存されたレコードの ID が古いままになってしまうので正常に保存されたレコードの ID を更新する
      // eslint-disable-next-line sonarjs/todo-tag
      // TODO: 正常保存レコードが見つからなかった時にエラーを出すかどうか決める
      if (Object.keys(temporaryToRealRecordIdMap).length > 0 && errors.length > 0) {
        const newRecords: ViewQueryRecordNode[] = []
        const alreadyReplacedRecordIds = new Set<string>()
        for (const oldRecord of records) {
          if (alreadyReplacedRecordIds.size === Object.keys(temporaryToRealRecordIdMap).length) {
            break
          }
          newRecords.push(replaceRecordId(oldRecord, temporaryToRealRecordIdMap, alreadyReplacedRecordIds, 0))
        }
        setRecords([...newRecords, ...records.slice(newRecords.length)])
      }

      setRecordSaveErrors([...errors])
      throw new Error([...errorSummaries, ...errors.map((e) => e.error)].join(`\n`))
    },
    addChange(changeHistory: ChangeHistory) {
      if (changeHistory.changes.length === 0) {
        return
      }

      setChanges((oldChanges) => {
        const newChanges = [...oldChanges, changeHistory]
        return newChanges
      })
      setBackedChanges([])
    },
    removeChanges(removeIds: string[]) {
      setChanges((changes) =>
        changes.map((change) => ({
          ...change,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          changes: change.changes.filter((x) => !removeIds.includes(x.id!)),
        })),
      )
    },
  }
}
export type LatestFieldChangeItem = ViewRecordFieldChangeItem & {
  streamName: string
}

export const buildLatestFieldChanges = (
  changes: ChangeHistory[],
): HashMap.HashMap<string, HashMap.HashMap<string, LatestFieldChangeItem>> =>
  pipe(
    changes,
    Array.flatMap((x) => x.changes),
    Array.flatMap((x) => x.fieldChanges.map((fc) => ({ ...fc, streamName: x.streamName }))),
    Array.reverse,
    Array.dedupeWith((a, b) => a.id === b.id && a.streamName === b.streamName && a.fieldName === b.fieldName),
    Array.groupBy((x) => x.id),
    Record.collect((key, value): [string, HashMap.HashMap<string, LatestFieldChangeItem>] => [
      key,
      HashMap.fromIterable(Array.map(value, (fc) => [`${fc.id},${fc.streamName},${fc.fieldName}`, fc])),
    ]),
    HashMap.fromIterable,
  )

export const useGetLatestFieldChanges = (): ((
  rowRecordIds: string[],
) => HashMap.HashMap<string, LatestFieldChangeItem>) => {
  const changes = useRecoilValue(changesAtom)

  const latestFieldChanges = useMemo(() => buildLatestFieldChanges(changes), [changes])

  return useCallback(
    (rowRecordIds: string[]) =>
      pipe(
        rowRecordIds,
        Array.flatMap((recordId) =>
          pipe(
            HashMap.get(latestFieldChanges, recordId),
            Option.map((changes) => HashMap.toEntries(changes)),
            Option.getOrElse(Array.empty),
          ),
        ),
        HashMap.fromIterable,
      ),
    [latestFieldChanges],
  )
}
